import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { hasFinePointer } from '@/lib/motion'

/**
 * A brass disc that follows the pointer and shows a verb ("View", "Read", "Zoom")
 * over anything marked with data-cursor. Desktop pointers only.
 */
const Cursor: FC = (): ReactNode => {
    const router = useRouter()
    const root = useRef<HTMLDivElement>(null)
    const label = useRef<HTMLSpanElement>(null)
    const current = useRef<string | null>(null)

    useEffect(() => {
        const element = root.current

        if (!element || !hasFinePointer()) return

        const xTo = gsap.quickTo(element, 'x', { duration: 0.55, ease: 'expo.out' })
        const yTo = gsap.quickTo(element, 'y', { duration: 0.55, ease: 'expo.out' })
        const pointer = { x: -200, y: -200 }

        const show = (text: string | null): void => {
            if (text === current.current) return

            current.current = text

            if (text && label.current) {
                label.current.textContent = text
                gsap.to(element, { scale: 1, autoAlpha: 1, duration: 0.5, ease: 'expo.out', overwrite: 'auto' })
            } else {
                gsap.to(element, { scale: 0, autoAlpha: 0, duration: 0.35, ease: 'expo.out', overwrite: 'auto' })
            }
        }

        const inspect = (target: Element | null): void => {
            const host = target?.closest?.('[data-cursor]')

            show(host?.getAttribute('data-cursor') ?? null)
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return

            pointer.x = event.clientX
            pointer.y = event.clientY
            xTo(event.clientX)
            yTo(event.clientY)
            inspect(event.target as Element | null)
        }

        const onScroll = (): void => {
            inspect(document.elementFromPoint(pointer.x, pointer.y))
        }

        const onLeave = (): void => show(null)

        gsap.set(element, { xPercent: -50, yPercent: -50, scale: 0, autoAlpha: 0 })

        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('scroll', onScroll, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)

        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('scroll', onScroll)
            document.documentElement.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    useEffect(() => {
        const hide = (): void => {
            current.current = null

            if (root.current) gsap.to(root.current, { scale: 0, autoAlpha: 0, duration: 0.3 })
        }

        router.events.on('routeChangeStart', hide)

        return () => router.events.off('routeChangeStart', hide)
    }, [router])

    return (
        <div
            ref={root}
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[75] hidden size-24 items-center justify-center rounded-full bg-acid text-ink opacity-0 label-mono lg:flex'
        >
            <span ref={label} />
        </div>
    )
}

export default Cursor
